import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Logger from "../../../services/Logger";
import ColorService from "../../../services/ColorService";
import UtilService from "../../../services/UtilService";
import AdminCrossBoardService from "../../pzl_cross/board/AdminCrossBoardService";
import AdminGridBoardService from "../../pzl_grid/board/AdminGridBoardService";
import MiniPuzRow from "../MiniPuzRow";

const getLanguageCode = (language) => {
    const lang = language?.toLowerCase();
    if (lang === "french") return "FR";
    if (lang === "english") return "EN";
    return language ? language.substring(0, 2).toUpperCase() : null;
};

const normalizeForMatch = (s) =>
    (s || "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const findCharacter = (row, col, filledCoordinates) => {
    let found = "";
    if (filledCoordinates && Array.isArray(filledCoordinates)) {
        const validEntries = filledCoordinates.filter((c) => c && c.positions && Array.isArray(c.positions));
        found = validEntries
            .map((c) => c.positions)
            .map((positions) => {
                const matchingPosition = positions.find((p) => p && p.row === row && p.col === col);
                if (matchingPosition && matchingPosition.value) {
                    const char = matchingPosition.value;
                    const normalized = char.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                    return normalized.toUpperCase();
                }
                return "";
            })
            .find((value) => value !== "");
    }
    return found || "";
};

const initializeSquare = (filledCoordinates) => {
    const size = 12;
    const newSquare = Array.from({ length: size }, () => Array(size).fill(0));
    for (let row = 0; row < size; row++) {
        for (let col = 0; col < size; col++) {
            newSquare[row][col] = findCharacter(row, col, filledCoordinates);
        }
    }
    return newSquare;
};

export default function RemoveWordDialog({
    open,
    onClose,
    tplId,
    wordsToRemove = [],
    onWordRemoved,
    onRefresh,
    puzzleType = "cross"
}) {
    const [puzzle, setPuzzle] = useState(null);
    const [loading, setLoading] = useState(false);
    const [removingWord, setRemovingWord] = useState(null);
    const [selectedWord, setSelectedWord] = useState(null);
    const [testMode, setTestMode] = useState(false);

    const fetchPuzzle = useCallback(async () => {
        if (!tplId) return;
        setLoading(true);
        try {
            const result = puzzleType === "grid"
                ? await AdminGridBoardService.requestHwTpl(tplId)
                : await AdminCrossBoardService.requestCrossTpl(tplId);
            setPuzzle(result);
        } catch (err) {
            Logger.error("Error fetching puzzle for RemoveWordDialog:", err);
            setPuzzle(null);
        } finally {
            setLoading(false);
        }
    }, [tplId, puzzleType]);

    useEffect(() => {
        if (open && tplId) {
            fetchPuzzle();
            setSelectedWord(null);
            setTestMode(false);
        }
    }, [open, tplId, fetchPuzzle]);

    const filledCoordinates = useMemo(() => {
        if (!puzzle?.filledCoordinates) return [];
        try {
            return typeof puzzle.filledCoordinates === "string"
                ? JSON.parse(puzzle.filledCoordinates)
                : Array.isArray(puzzle.filledCoordinates)
                    ? puzzle.filledCoordinates
                    : [];
        } catch {
            return [];
        }
    }, [puzzle]);

    const coordinatesForPreview = useMemo(() => {
        if (testMode && selectedWord?.text) {
            const exclude = normalizeForMatch(selectedWord.text);
            return filledCoordinates.filter((c) => normalizeForMatch(c.text) !== exclude);
        }
        return filledCoordinates;
    }, [filledCoordinates, testMode, selectedWord?.text]);

    const square = useMemo(() => initializeSquare(coordinatesForPreview), [coordinatesForPreview]);

    const emptyCellCount = useMemo(() => {
        let count = 0;
        for (let r = 0; r < square.length; r++) {
            for (let c = 0; c < (square[r]?.length ?? 0); c++) {
                const val = square[r][c];
                if (!val || String(val).trim() === "") count++;
            }
        }
        return count;
    }, [square]);

    const wordsInPuzzle = useMemo(() => {
        return filledCoordinates
            .filter((c) => c.text && typeof c.text === "string")
            .map((c) => ({
                text: c.text.trim(),
                isScattered: c.direction === "scattered",
                positions: c.positions || []
            }))
            .sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base" }));
    }, [filledCoordinates]);

    const wordsToRemoveSet = useMemo(() => {
        const normalized = (wordsToRemove || [])
            .map((w) => normalizeForMatch(typeof w === "string" ? w : w?.text || ""))
            .filter(Boolean);
        return new Set(normalized);
    }, [wordsToRemove]);

    const handleRemoveWord = useCallback(async (wordItem) => {
        if (!puzzle?.id || !wordItem?.text) return;
        const wordText = wordItem.text.trim();
        setRemovingWord(wordText);
        try {
            const updated = filledCoordinates.filter((c) => {
                return normalizeForMatch(c.text) !== normalizeForMatch(wordText);
            });
            const updateService = puzzleType === "grid" ? AdminGridBoardService : AdminCrossBoardService;
            await updateService.updatePuzzleCoordinates(puzzle.id, updated, null);
            try {
                const language = puzzle.language || "english";
                await updateService.refreshWordNetwork(puzzle.id, language);
            } catch (refreshErr) {
                Logger.error("Error refreshing word network after word removal:", refreshErr);
            }
            onWordRemoved?.(wordText);
            onRefresh?.();
            setSelectedWord(null);
            setTestMode(false);
            await fetchPuzzle();
        } catch (err) {
            Logger.error("Error removing word:", err);
        } finally {
            setRemovingWord(null);
        }
    }, [puzzle?.id, filledCoordinates, onWordRemoved, onRefresh, fetchPuzzle, puzzleType]);

    const handleClose = () => {
        setSelectedWord(null);
        setTestMode(false);
        onClose?.();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Typography variant="h6" component="div">
                    Remove words
                </Typography>
                {puzzle && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
                        {/* Row 1: Language, Level, State, Type, Tags */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                            {getLanguageCode(puzzle.language) && (
                                <Chip
                                    label={getLanguageCode(puzzle.language)}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontWeight: "bold", minWidth: 32, height: 24 }}
                                />
                            )}
                            {puzzle.difficultyLevel && (
                                <Chip
                                    label={puzzle.difficultyLevel}
                                    size="small"
                                    sx={{
                                        backgroundColor: ColorService.getDifficultyColor(puzzle.difficultyLevel),
                                        color: "white",
                                        textTransform: "capitalize"
                                    }}
                                />
                            )}
                            {puzzle.state && (
                                <Chip
                                    label={String(puzzle.state).toUpperCase()}
                                    size="small"
                                    sx={{
                                        backgroundColor: ColorService.getStateColor(puzzle.state),
                                        color: "white",
                                        textTransform: "uppercase"
                                    }}
                                />
                            )}
                            {puzzleType === "cross" && puzzle.type === "CROSSWORD" && (
                                <Chip label="Crossword" size="small" variant="outlined" sx={{ textTransform: "none" }} />
                            )}
                            {puzzleType === "grid" && puzzle.type === "WORD_SEARCH" && (
                                <Chip label="Word Search" size="small" variant="outlined" sx={{ textTransform: "none" }} />
                            )}
                            {puzzleType === "grid" && puzzle.type === "HIDDEN_MESSAGE" && (
                                <Chip
                                    label={puzzle.hiddenMessageText ? `Hidden Message: ${puzzle.hiddenMessageText.toUpperCase()}` : "Hidden Message"}
                                    size="small"
                                    variant="outlined"
                                    sx={{ textTransform: "none", maxWidth: 280 }}
                                />
                            )}
                            {(() => {
                                const rawTags = puzzle.tags ?? puzzle.template?.tags ?? [];
                                const tags = Array.isArray(rawTags) ? rawTags : [];
                                const toStr = (t) => (typeof t === "string" ? t : (t?.name ?? t?.label ?? String(t ?? "")));
                                const filtered = tags.filter((t) => !toStr(t).toLowerCase().includes("bedrock"));
                                if (filtered.length === 0) return null;
                                return (
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {filtered.map((tag, index) => (
                                            <Chip key={index} label={toStr(tag)} size="small" variant="outlined" sx={{ fontSize: "0.75rem", height: "24px" }} />
                                        ))}
                                    </Box>
                                );
                            })()}
                        </Box>
                        {/* Row 2: Title */}
                        <Typography variant="h5" component="div" sx={{ fontWeight: "bold", color: "primary.main" }}>
                            {puzzle.title || "Puzzle"}
                        </Typography>
                        {/* Row 3: Created at, Updated at */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <Typography variant="body2" color="text.secondary">
                                Created: {puzzle.createdAt
                                    ? (() => {
                                        const now = Date.now();
                                        const ts = typeof puzzle.createdAt === "number" ? puzzle.createdAt : new Date(puzzle.createdAt).getTime();
                                        return UtilService.formatHumanizeDetailed(now - ts) || "Recently";
                                    })()
                                    : "Unknown"}
                            </Typography>
                            {puzzle.updatedAt && (
                                <Typography variant="body2" color="text.secondary">
                                    • Updated: {(() => {
                                        const now = Date.now();
                                        const ts = typeof puzzle.updatedAt === "number" ? puzzle.updatedAt : new Date(puzzle.updatedAt).getTime();
                                        return UtilService.formatHumanizeDetailed(now - ts) || "Recently";
                                    })()}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                        <CircularProgress />
                        <span>Loading puzzle…</span>
                    </Box>
                ) : !puzzle ? (
                    <Typography color="text.secondary" sx={{ py: 2 }}>
                        Failed to load puzzle.
                    </Typography>
                ) : (
                    <>
                    <Grid container spacing={3} sx={{ mt: 0 }}>
                        {/* Puzzle Preview Grid */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, flexWrap: "wrap" }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#3BA666" }}>
                                    Puzzle Preview
                                </Typography>
                                {testMode && selectedWord && (
                                    <Typography variant="caption" color="warning.main" sx={{ fontStyle: "italic" }}>
                                        Test: &quot;{selectedWord.text}&quot; hidden from grid
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 1,
                                    p: 1,
                                    backgroundColor: "#fafafa",
                                    overflow: "hidden"
                                }}
                            >
                                {square.length > 0 && square[0]?.length > 0 ? (
                                    <Box>
                                        <Box sx={{ display: "flex", gap: "1px", mb: 0.5 }}>
                                            <Box sx={{ width: 24, height: 24 }} />
                                            {Array.from({ length: 12 }, (_, colIndex) => (
                                                <Box
                                                    key={colIndex}
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "9px",
                                                        fontWeight: "bold",
                                                        color: "primary.main"
                                                    }}
                                                >
                                                    {colIndex}
                                                </Box>
                                            ))}
                                        </Box>
                                        {square.map((row, rowIndex) => (
                                            <Box key={rowIndex} sx={{ display: "flex", alignItems: "center", gap: "1px" }}>
                                                <Box
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: "9px",
                                                        fontWeight: "bold",
                                                        color: "primary.main"
                                                    }}
                                                >
                                                    {rowIndex}
                                                </Box>
                                                <MiniPuzRow
                                                    rowIndex={rowIndex}
                                                    row={row}
                                                    hiddenWord={selectedWord?.text || ""}
                                                    positions={selectedWord?.positions || []}
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography color="text.secondary" sx={{ p: 2 }}>
                                        No puzzle grid data available
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        {/* Words in puzzle */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "#3BA666", mb: 1 }}>
                                Words in puzzle
                            </Typography>
                            {wordsToRemoveSet.size > 0 && (
                                <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                                    Words marked with * have no exclusive cells and should be removed.
                                </Typography>
                            )}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 320, overflow: "auto" }}>
                                {wordsInPuzzle.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No words in puzzle
                                    </Typography>
                                ) : (
                                    wordsInPuzzle.map((item, idx) => {
                                        const isSelected = selectedWord?.text?.toLowerCase() === item.text.toLowerCase();
                                        const shouldRemove = wordsToRemoveSet.has(normalizeForMatch(item.text));
                                        return (
                                            <Chip
                                                key={idx}
                                                label={
                                                    <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                        {shouldRemove && <span style={{ color: "#ed6c02" }}>*</span>}
                                                        {item.isScattered ? `"${item.text.toLowerCase()}" (hidden)` : item.text.toLowerCase()}
                                                    </Box>
                                                }
                                                size="small"
                                                variant={isSelected ? "filled" : item.isScattered ? "filled" : "outlined"}
                                                color={isSelected ? "primary" : item.isScattered ? "secondary" : shouldRemove ? "warning" : "default"}
                                                onClick={() => setSelectedWord(isSelected ? null : item)}
                                                sx={{
                                                    cursor: "pointer",
                                                    mb: 0.5,
                                                    ...(isSelected && {
                                                        fontWeight: 700,
                                                        boxShadow: 2,
                                                        border: "2px solid",
                                                        borderColor: "primary.main"
                                                    })
                                                }}
                                            />
                                        );
                                    })
                                )}
                            </Box>
                            {selectedWord && (
                                <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <Tooltip title="Preview how the grid looks without this word">
                                        <span>
                                            <Button
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                startIcon={<VisibilityIcon />}
                                                onClick={() => setTestMode((prev) => !prev)}
                                            >
                                                {testMode ? "Show word" : "TEST"}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip
                                        title={
                                            wordsToRemoveSet.has(normalizeForMatch(selectedWord.text))
                                                ? "Remove this word from the puzzle"
                                                : "Only words marked with * (no exclusive cells) can be removed"
                                        }
                                    >
                                        <span>
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                startIcon={
                                                    removingWord === selectedWord.text ? (
                                                        <CircularProgress size={16} color="inherit" />
                                                    ) : (
                                                        <DeleteIcon />
                                                    )
                                                }
                                                onClick={() => handleRemoveWord(selectedWord)}
                                                disabled={!!removingWord || !wordsToRemoveSet.has(normalizeForMatch(selectedWord.text))}
                                            >
                                                Remove &quot;{selectedWord.text}&quot;
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                    {emptyCellCount > 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {emptyCellCount === 1 ? "There is an empty cell" : "There are empty cells"}
                        </Alert>
                    )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
